import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { Download, Upload } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { usePulseImports } from '@/hooks/use-pulse-imports';
import { saveFile } from '@/services/order-samples.service';
import { pulseImportService } from '@/services/pulse-onboarding.service';
import type { PulseImport } from '@/types';
import { formatIst } from '../test-orders';
import { describeImport, isImportRunning, summarizeImport } from '../pulse-import';
import { PulseImportSheet } from './pulse-import-sheet';

const STATUS_BADGE: Record<PulseImport['status'], { label: string; variant: 'default' | 'success' | 'destructive' }> = {
  queued: { label: 'Queued', variant: 'default' },
  running: { label: 'Running', variant: 'default' },
  completed: { label: 'Done', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' }
};

function ImportRow({ pulseImport, onDownload, downloading }: {
  pulseImport: PulseImport;
  onDownload: () => void;
  downloading: boolean;
}) {
  const status = STATUS_BADGE[pulseImport.status];
  return (
    <li className='space-y-1 border-t py-3 first:border-t-0'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <span className='text-sm'>
          {formatIst(pulseImport.started_at)}
          {pulseImport.requested_by ? ` · ${pulseImport.requested_by}` : ''}
        </span>
        <Badge variant={status.variant}>
          {isImportRunning(pulseImport) && <Spinner className='size-3' />}
          {status.label}
        </Badge>
      </div>
      <p className='text-xs text-muted-foreground'>{describeImport(pulseImport)}</p>
      <p className='text-xs'>{summarizeImport(pulseImport)}</p>
      {pulseImport.error && <p className='text-xs text-red-foreground'>{pulseImport.error}</p>}
      {pulseImport.mode === 'ids' && (
        <Button variant='link' size='sm' className='h-auto p-0 text-xs' onClick={onDownload} disabled={downloading}>
          <Download className='size-3.5' /> Download results
        </Button>
      )}
    </li>
  );
}

/**
 * The Pulse card's Import tickets button and the account's import history. An import that is
 * still running is followed until it settles.
 */
export function PulseImportHistory() {
  const accountId = useAtomValue(selectedAccountIdAtom);
  const { data: imports = [], isLoading, error } = usePulseImports();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  const download = async (importId: string) => {
    if (accountId == null) return;
    setDownloading(importId);
    try {
      const { blob, fileName } = await pulseImportService.results(accountId, importId);
      saveFile(blob, fileName);
    } catch (downloadError) {
      console.error('PulseImportHistory: results download failed', { importId, downloadError });
      toast.error(
        downloadError instanceof Error && downloadError.message
          ? downloadError.message
          : "Couldn't download the results."
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className='space-y-3'>
      <Button size='sm' variant='outline' className='gap-1.5' onClick={() => setOpen(true)}>
        <Upload className='size-3.5' />
        Import tickets
      </Button>
      <p className='text-xs text-muted-foreground'>
        Brings chosen tickets into Pulse: by closed date, by the intents KIM saw, or by a list of
        ticket IDs.
      </p>
      {isLoading ? (
        <Skeleton className='h-16 w-full' />
      ) : error ? (
        <p className='text-xs text-red-foreground'>Couldn't load the imports.</p>
      ) : imports.length > 0 ? (
        <ul className='rounded-md border px-3'>
          {imports.map((pulseImport) => (
            <ImportRow
              key={pulseImport._id}
              pulseImport={pulseImport}
              downloading={downloading === pulseImport._id}
              onDownload={() => download(pulseImport._id)}
            />
          ))}
        </ul>
      ) : null}
      <PulseImportSheet open={open} onOpenChange={setOpen} />
    </div>
  );
}
